import { useState, useEffect, useMemo } from 'react';

import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { useNavigate, useLocation } from 'react-router';
import { useMount, useUpdateEffect } from 'react-use';
import { Cell } from 'react-table';
import {
  Row,
  Col,
  Button,
  OverlayTrigger,
  Tooltip,
  Container,
  DropdownButton,
  Dropdown as DropdownBS,
  Badge,
  Alert
} from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { Nav } from 'react-bootstrap';
import classNames from 'classnames';
import qs from 'qs';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';

import { PermissionDenied } from 'components/Status';
import { useAxios } from 'configs/requests';
import { Error } from 'components/Fetch';
import { CheckIcon, DownloadIcon, ThreeVerticalDotsIcon, UploadIcon } from 'assets/icons';
import { CopyableText, Table, ToastNotificationContent, PortalDropdownMenu } from 'components/UI';
import { getPath } from 'routes';
import { DeviceTypes, DeviceStatus, AccessRoles, PatientStatus } from 'constants/enums';
import {
  formatDeliveryWindow,
  getDeviceStatusValue,
  isAmazonFulfilled,
  isDeviceBatteryLow
} from 'utils/Devices';
import { getAmazonTrackingUrl, getTrackingUrl } from 'utils/tracking-url';
import { getFulfillmentTrack, getLatestPreShipmentEvent } from 'utils/fulfillment';
import { Dropdown } from 'components/Forms';
import {
  getDeviceConditionOptions,
  getDeviceLocationOptions,
  getDeviceStatusOptions,
  TABLE_EMPTY_VALUE,
  actionableDeviceManufacturers,
  resyncableDeviceManufacturers,
  BODYTRACE_MANUFACTURER_NAME
} from 'constants/constants';
import {
  getAuthorizedSubgroups,
  getCurrentOrg,
  getCurrentOrgSubgroups,
  groupHasDeviceMgmtDisabled,
  selectCurrentGid
} from 'store/groups/reducer';
import { updateDevices } from 'store/patient/reducer';
import { onChangeSelection } from 'store/devices/reducer';
import { useCanMigratePatients, usePagination, useRole } from 'hooks';
import { formatDropdownOptions } from 'utils/Object';
import { getUserRoles } from 'store/oauth/reducer';
import { useAppSelector } from 'hooks';
import { ExternalLink } from 'components/UI/ExternalLink';
import { MigrateDevicesModal } from 'pages/Dashboard/PatientMigration';
import { getMigrationStatus, isMigrationOutstanding } from 'utils/PatientMigration';

import { Search } from '../Filters';
import BulkOrderAccessories from './BulkOrderAccessories';
import BulkOrderDevices from './BulkOrderDevices';
import AddDevice from './AddDevice';
import UnassignDevice from './UnassignDevice';
import ReplaceDeviceModal from './ReplaceDeviceModal';
import ResyncDeviceModal from './ResyncDeviceModal';
import DeleteDevice from './DeleteDevice';
import DeviceHistory from './DeviceHistory';
import DeviceFilters from './DeviceFilters';
import ActiveDeviceFiltersDisplay from './ActiveDeviceFiltersDisplay';
import ReplaceImeiModal from './ReplaceImeiModal';
import ActivateSimModal from './ActivateSimModal';
import ShippingConfirmationModal from './ShippingConfirmationModal';
import { ExportPendingOrdersModal } from './ExportPendingOrdersModal';
import { RestoreDeviceModal } from './RestoreDeviceModal';
import CancelOrderModal from './CancelOrderModal';
import AccessoriesModal from './AccessoriesModal';
import { FulfillmentStatusLine } from './FulfillmentStatusLine';

type OrderBy =
  | 'PATIENT_NAME'
  | 'DEVICE_NAME'
  | 'BATTERY_LEVEL'
  | 'ASSIGNED_DATE'
  | 'GROUP'
  | 'IMEI'
  | 'MRN'
  | 'DOB'
  | 'TRACKING_NUMBER'
  | 'PROMISED_DELIVERY'
  | 'PURCHASE_ORDER'
  | 'DEVICE_TYPE'
  | 'LOCATION'
  | 'CONDITION'
  | 'SIGNAL_STRENGTH';

type DeviceAction = 'replace' | 'resync' | 'register' | 'activateSim';

interface DevicesProps {
  patient?: IPatient | null;
  showActions?: boolean;
}

const Devices = ({ showActions = true, patient }: DevicesProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const selectedGid = useAppSelector(selectCurrentGid);
  const patientGid = useAppSelector((state) => state.patient.data?.gid);
  const locale = useAppSelector((state) => state.intl.locale);
  const dispatch = useDispatch();
  const { page, setPage, limit, setLimit } = usePagination();
  const [orderBy, setOrderBy] = useState<OrderBy>('DEVICE_NAME');
  const [desc, setDesc] = useState<boolean>(false);
  const [unassignDevice, setUnassignDevice] = useState<IDeviceListItem | null>(null);
  const [showMigrateDevices, setShowMigrateDevices] = useState(false);
  const canMigratePatients = useCanMigratePatients();
  const isMigratingAway = isMigrationOutstanding(getMigrationStatus(patient));
  const [deletedDeviceId, setDeletedDeviceId] = useState<string | null>(null);
  const [bulkDeleted, setBulkDeleted] = useState<IDeviceListItem[]>([]);
  const [searchDevices, setSearchDevices] = useState<string>('');
  const { deviceType } = useParams<{ deviceType: DeviceTypes }>();
  const gid = selectedGid || patientGid;
  const deviceMgmtDisabledForGroup = useAppSelector((state) => groupHasDeviceMgmtDisabled(state));
  const [devices, setDevices] = useState<IDeviceListItem[]>([]);
  const userRoles = useAppSelector((state) => getUserRoles(state)) || [];
  const isSuperUser = canMigratePatients;
  const isSysop = userRoles.includes(AccessRoles.SYSOP);
  const isProfile = !!patient;
  const [filters, setFilters] = useState<IDeviceTemplateFilters[]>([]);
  const [deviceToReplace, setDeviceToReplace] = useState<IDeviceListItem | null>(null);
  const [deviceToReplaceImei, setDeviceToReplaceImei] = useState<IDeviceListItem | null>(null);
  const [deviceToActivateSim, setDeviceToActivateSim] = useState<IDeviceListItem | null>(null);
  const [deviceToResync, setDeviceToResync] = useState<IDeviceListItem | null>(null);
  const [deviceToRestore, setDeviceToRestore] = useState<IDeviceListItem | null>(null);
  const [orderToCancel, setOrderToCancel] = useState<IDeviceListItem | null>(null);
  const [deviceForAccessories, setDeviceForAccessories] = useState<IDeviceListItem | null>(null);
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showBulkOrderModal, setShowBulkOrderModal] = useState<boolean>(false);
  const [showAccessoriesOrderModal, setShowAccessoriesOrderModal] = useState<boolean>(false);
  const [showShippingConfirmationModal, setShowShippingConfirmationModal] =
    useState<boolean>(false);
  const [showExportOrdersModal, setShowExportOrdersModal] = useState<boolean>(false);
  const [refetch, setRefetch] = useState<boolean>(false);
  const [totalDevicesCount, setTotalDevicesCount] = useState<number | null>(null);
  const canBulkOrder = useRole([AccessRoles.GROUP_MANAGEMENT]) || isSuperUser;
  const bulkOrderDisabled = gid === 'all' || !canBulkOrder;
  const deviceOrderingsAvailable = useAppSelector((state) => state.groups.deviceOrderingsAvailable);
  const hasOrderableAccessories = deviceOrderingsAvailable.some(
    (d) => !!d.parentDeviceOrderingId && !d.isDefaultAccessory
  );
  const currentOrg = useAppSelector(getCurrentOrg);
  const currentOrgSubgroups = useAppSelector(getCurrentOrgSubgroups);
  const allSubgroups = useAppSelector(getAuthorizedSubgroups);
  let subgroupsAsOptions: ISelectOption[] = [];
  if (!currentOrg) {
    // If the user has access to subgroups only (no org), allow them to
    // reassign a device to any of those subgroups
    if (allSubgroups.length > 1) {
      subgroupsAsOptions = formatDropdownOptions(allSubgroups, 'gid', 'name');
    }
  } else {
    // If the user has access to many orgs and current group selection is 'all',
    //  allow them to reassign a device to any subgroup
    if (currentOrg.gid === 'all') {
      subgroupsAsOptions = formatDropdownOptions(allSubgroups, 'gid', 'name');
    }
    // If the user has access to >=1 org, allow them to reassign a device
    // only if they also have access to more than 1 subgroup belonging to the current org
    else if (currentOrgSubgroups && currentOrgSubgroups?.length > 1) {
      subgroupsAsOptions = formatDropdownOptions(currentOrgSubgroups, 'gid', 'name');
    }
  }

  const getParams = () => {
    const params: Record<string, unknown> = {
      filter: deviceType?.toUpperCase(),
      limit,
      page: page + 1,
      sort: `${desc ? '-' : '+'}${orderBy}`
    };
    if (searchDevices) params.searchCriteria = searchDevices;

    const subgroupsFilter = filters.find((filter) => filter.filterLabel === 'subgroups');
    if (subgroupsFilter) {
      params.groupIds = subgroupsFilter.filterValue;
    }

    const parsedFilters = filters
      .filter((filter) => filter.filterLabel !== 'subgroups')
      .reduce((acc: Record<string, unknown>, filter) => {
        acc[filter.filterLabel] =
          filter.filterLabel === 'batteryLevel' ? filter.filterValue?.[0] : filter.filterValue;
        return acc;
      }, {});

    return {
      ...params,
      ...parsedFilters
    };
  };

  const [, register] = useAxios(
    {
      method: 'POST'
    },
    { manual: true }
  );

  const isAllGroups = !isProfile && gid === 'all';

  const [{ data: rawData, loading, error }, get] = useAxios<
    IDeviceListItem[] | IDeviceListResponse
  >(
    {
      method: 'GET',
      url: isProfile
        ? `/clinician-gateway/patient/${patient?.uid}/device`
        : `/clinician-gateway/group/${gid}/device?${qs.stringify(getParams())}`
    },
    {
      useCache: false,
      manual: isAllGroups
    }
  );

  const data = useMemo(() => {
    if (!rawData) return undefined;
    if (Array.isArray(rawData)) {
      return {
        data: rawData,
        total: rawData.length,
        totalPages: 1
      };
    }
    return {
      data: rawData.data,
      total: rawData.total,
      totalPages: Math.ceil(rawData.total / rawData.limit)
    };
  }, [rawData]);

  const [{ loading: loadingDeviceUpdate }, patchDeviceUpdate] = useAxios(
    {
      method: 'PATCH'
    },
    { manual: true }
  );

  const [{ loading: reassignGroupLoading }, reassignGroup] = useAxios(
    {
      method: 'PATCH'
    },
    { manual: true }
  );

  // verify if any devices in the list allow replacements / resyncs
  const isDeviceActionable = (device: IDeviceListItem, action: DeviceAction) => {
    const manufacturer = device.deviceOrdering?.manufacturer ?? '';
    const series = device.deviceOrdering?.series;
    const isGen3Direct = series === 'direct' || series === 'direct_plus';
    switch (action) {
      case 'replace':
        return isGen3Direct || actionableDeviceManufacturers.includes(manufacturer);
      case 'register':
        return isGen3Direct || [BODYTRACE_MANUFACTURER_NAME].includes(manufacturer);
      case 'resync':
        return isGen3Direct || resyncableDeviceManufacturers.includes(manufacturer);
      case 'activateSim':
        return isGen3Direct;
      default:
        return false;
    }
  };

  const handleSearch = (value?: string) => {
    setSearchDevices(value || '');
    setPage(0);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setRefetch(true);
  };

  const handleSorting = (newOrderBy: OrderBy) => {
    if (orderBy === newOrderBy) {
      setDesc(!desc);
    } else {
      setOrderBy(newOrderBy);
      setDesc(true);
    }
  };

  const registerDevice = async (device: IDeviceListItem) => {
    await register({
      url: `/clinician-gateway/device/${device.deviceId}/register`
    })
      .then(() => {
        toast(
          <ToastNotificationContent
            icon={<CheckIcon className="icon" />}
            text="deviceRegisterSuccessKey"
          />
        );
      })
      .catch(() => {
        toast.error(
          <ToastNotificationContent
            icon={<CheckIcon className="icon" />}
            text="deviceRegisterErrorKey"
          />
        );
      });
  };

  const handleDeviceChange = (
    option: ISelectOption,
    deviceId: string,
    type: 'location' | 'condition'
  ) => {
    const data: Record<string, string> = {};
    if (type === 'location') data.location = option.value;
    if (type === 'condition') data.condition = option.value;
    patchDeviceUpdate({ url: `/clinician-gateway/device/${deviceId}`, data }).then(() => {
      const newDevices = devices.map((device) => {
        const newDevice = { ...device };
        if (device.deviceId === deviceId) {
          if (type === 'location') newDevice.location = option.value;
          if (type === 'condition') newDevice.condition = option.value;
        }
        return newDevice;
      });
      setDevices(newDevices);
    });
  };

  const navigateToDevice = (deviceId: string) => {
    navigate(getPath().dashboard.device(deviceId));
  };

  const handleReassignGroup = (device: IDeviceListItem, newGid: string) => {
    const data: Record<string, string> = { gid: newGid };

    reassignGroup({ url: `/clinician-gateway/device/${device.deviceId}`, data });

    setDevices(devices.map((d) => (d.deviceId === device.deviceId ? { ...d, gid: newGid } : d)));
  };

  const clearFilters = (filtersLabels: IDeviceFilterLabel[]) => {
    return setFilters(filters.filter((filter) => !filtersLabels.includes(filter.filterLabel)));
  };

  const extractTrackingUrl = (device: IDeviceListItem, trackingNumber: string) => {
    if (isAmazonFulfilled(device.deviceOrdering.shippingProvider)) {
      return getAmazonTrackingUrl(trackingNumber);
    }
    return getTrackingUrl(trackingNumber);
  };

  const columns = [
    {
      Header: intl.formatMessage({ id: 'typeKey' }),
      order: orderBy === 'DEVICE_TYPE' ? (!desc ? 'asc' : 'desc') : null,
      sort: !isProfile ? () => handleSorting('DEVICE_TYPE') : undefined,
      Cell: ({ row: { original } }: Cell<IDeviceListItem>) => {
        return original.deviceOrdering?.displayType
          ? intl.formatMessage({ id: original.deviceOrdering.displayType })
          : TABLE_EMPTY_VALUE;
      }
    },
    {
      Header:
        deviceType === DeviceTypes.ASSIGNED
          ? intl.formatMessage({ id: 'patientNameKey' })
          : intl.formatMessage({ id: 'previousPatientKey' }),
      order: orderBy === 'PATIENT_NAME' ? (!desc ? 'asc' : 'desc') : null,
      accessor: 'name',
      hide: isProfile,
      sort: () => handleSorting('PATIENT_NAME'),
      Cell: ({ row: { original } }: Cell<IDeviceListItem>) => {
        let fullName = '';
        if (original.patient?.lastName) fullName += original.patient.lastName;
        if (original.patient?.firstName) fullName += ', ' + original.patient.firstName;
        return fullName ? <CopyableText>{fullName}</CopyableText> : TABLE_EMPTY_VALUE;
      }
    },
    {
      Header: intl.formatMessage({ id: 'dobKey' }),
      order: orderBy === 'DOB' ? (!desc ? 'asc' : 'desc') : null,
      align: 'right',
      hide: deviceType === DeviceTypes.UNASSIGNED || isProfile,
      accessor: 'dob',
      sort: () => handleSorting('DOB'),
      Cell: ({ row: { original } }: Cell<IDeviceListItem>) => {
        return original.patient?.birthday
          ? DateTime.fromISO(original.patient.birthday)
              .setLocale(locale)
              .toLocaleString(DateTime.DATE_SHORT)
          : TABLE_EMPTY_VALUE;
      }
    },
    {
      Header: intl.formatMessage({ id: 'alternateIdKey' }),
      order: orderBy === 'MRN' ? (!desc ? 'asc' : 'desc') : null,
      hide: deviceType === DeviceTypes.UNASSIGNED || isProfile,
      accessor: 'mrn',
      sort: () => handleSorting('MRN'),
      Cell: ({ row: { original } }: Cell<IDeviceListItem>) => {
        return <span>{original.patient?.alternateId || TABLE_EMPTY_VALUE}</span>;
      }
    },
    {
      Header: intl.formatMessage({ id: 'groupsKey' }),
      order: orderBy === 'GROUP' ? (!desc ? 'asc' : 'desc') : null,
      hide: isProfile,
      accessor: 'group',
      width: 190,
      sort: () => handleSorting('GROUP'),
      Cell: ({ row: { original } }: Cell<IDeviceListItem>) => {
        const groupName = allSubgroups.find((g) => g.gid === original.gid)?.name;

        if (subgroupsAsOptions.length && deviceType === DeviceTypes.UNASSIGNED) {
          const defaultGroup = subgroupsAsOptions.find((o) => o.value === original.gid);
          return (
            <div>
              <Dropdown
                minWidth={175}
                menuPosition="fixed"
                options={subgroupsAsOptions}
                defaultValue={defaultGroup}
                variant="transparent"
                onChange={(option: ISelectOption) => handleReassignGroup(original, option.value)}
                isDisabled={reassignGroupLoading}
                containerNoWrap
                dataTestId={`device-group-${original.deviceId}`}
              />
            </div>
          );
        }

        const displayName = groupName || TABLE_EMPTY_VALUE;
        if (groupName && groupName.length > 15) {
          return (
            <OverlayTrigger overlay={<Tooltip id={original.deviceId}>{groupName}</Tooltip>}>
              <div>{displayName}</div>
            </OverlayTrigger>
          );
        }

        return <div>{displayName}</div>;
      }
    },
    {
      Header: intl.formatMessage({ id: 'imeiKey' }),
      order: orderBy === 'IMEI' ? (!desc ? 'asc' : 'desc') : null,
      sort: !isProfile ? () => handleSorting('IMEI') : undefined,
      Cell: ({ row: { original } }: Cell<IDeviceListItem>) => {
        return original.imei ? <CopyableText>{original.imei}</CopyableText> : TABLE_EMPTY_VALUE;
      }
    },
    {
      Header: intl.formatMessage({ id: 'deviceKey' }),
      order: orderBy === 'DEVICE_NAME' ? (!desc ? 'asc' : 'desc') : null,
      sort: !isProfile ? () => handleSorting('DEVICE_NAME') : undefined,
      Cell: ({ row: { original } }: Cell<IDeviceListItem>) => {
        const displayName =
          original.deviceOrdering?.parent?.displayName ?? original.deviceOrdering?.displayName;
        return displayName ? intl.formatMessage({ id: displayName }) : TABLE_EMPTY_VALUE;
      }
    },
    {
      Header: intl.formatMessage({ id: 'processDateKey' }),
      align: 'right',
      order: orderBy === 'ASSIGNED_DATE' ? (!desc ? 'asc' : 'desc') : null,
      accessor: 'assignedDate',
      hide: deviceType === DeviceTypes.UNASSIGNED,
      sort: !isProfile ? () => handleSorting('ASSIGNED_DATE') : undefined,
      Cell: ({ row: { original } }: Cell<IDeviceListItem>) => {
        return original.assignedDate
          ? DateTime.fromISO(original.assignedDate)
              .setLocale(locale)
              .toLocaleString(DateTime.DATE_SHORT)
          : TABLE_EMPTY_VALUE;
      }
    },
    {
      Header: intl.formatMessage({ id: 'trackingNumberKey' }),
      order: orderBy === 'TRACKING_NUMBER' ? (!desc ? 'asc' : 'desc') : null,
      accessor: 'trackingInfo',
      sort: !isProfile ? () => handleSorting('TRACKING_NUMBER') : undefined,
      Cell: ({ row: { original } }: Cell<IDeviceListItem>) => {
        const trackingNumber = original.shippingRecord?.trackingNumber;
        if (!trackingNumber) return TABLE_EMPTY_VALUE;
        const trackingUrl = extractTrackingUrl(original, trackingNumber);
        return trackingUrl ? (
          <ExternalLink
            href={trackingUrl}
            title={trackingNumber}
            data-test={`tracking-number-${original.deviceId}`}
          >
            {trackingNumber}
          </ExternalLink>
        ) : (
          <span data-test={`tracking-number-${original.deviceId}`}>{trackingNumber}</span>
        );
      }
    },
    {
      Header: intl.formatMessage({ id: 'devices.estDelivery' }),
      order: orderBy === 'PROMISED_DELIVERY' ? (!desc ? 'asc' : 'desc') : null,
      accessor: 'promisedDelivery',
      sort: !isProfile ? () => handleSorting('PROMISED_DELIVERY') : undefined,
      Cell: ({ row: { original } }: Cell<IDeviceListItem>) => {
        const window = original.purchaseOrder?.promisedDelivery;
        const text = window ? formatDeliveryWindow(window.start, window.end, locale) : null;
        return (
          <span
            data-test={`device-est-delivery-${original.deviceId}`}
            data-test-value={window?.source}
          >
            {text ?? TABLE_EMPTY_VALUE}
          </span>
        );
      }
    },
    {
      Header: intl.formatMessage({ id: 'purchaseOrderKey' }),
      order: orderBy === 'PURCHASE_ORDER' ? (!desc ? 'asc' : 'desc') : null,
      sort: !isProfile ? () => handleSorting('PURCHASE_ORDER') : undefined,
      Cell: ({ row: { original } }: Cell<IDeviceListItem>) => {
        return original.purchaseOrder?.displayId
          ? original.purchaseOrder.displayId
          : TABLE_EMPTY_VALUE;
      }
    },
    {
      Header: intl.formatMessage({ id: 'locationKey' }),
      order: orderBy === 'LOCATION' ? (!desc ? 'asc' : 'desc') : null,
      sort: !isProfile ? () => handleSorting('LOCATION') : undefined,
      Cell: ({ row: { original } }: Cell<IDeviceListItem>) => {
        const locationOptions = getDeviceLocationOptions();
        const defaultValue =
          locationOptions.find((item) => item.value === original.location) || locationOptions[0];
        return (
          <Dropdown
            dataTestId={`device-location-${original.deviceId}`}
            minWidth={175}
            menuPosition="fixed"
            options={locationOptions}
            defaultValue={defaultValue}
            variant="transparent"
            onChange={(option: ISelectOption) =>
              handleDeviceChange(option, original.deviceId, 'location')
            }
            isDisabled={
              loadingDeviceUpdate || (original.status === DeviceStatus.REPLACED && !isSuperUser)
            }
            containerNoWrap
          />
        );
      }
    },
    {
      Header: intl.formatMessage({ id: 'conditionKey' }),
      order: orderBy === 'CONDITION' ? (!desc ? 'asc' : 'desc') : null,
      sort: !isProfile ? () => handleSorting('CONDITION') : undefined,
      Cell: ({ row: { original } }: Cell<IDeviceListItem>) => {
        const conditionOptions = getDeviceConditionOptions();
        const defaultValue =
          conditionOptions.find((item) => item.value === original.condition) || conditionOptions[0];
        return (
          <Dropdown
            dataTestId={`device-condition-${original.deviceId}`}
            minWidth={175}
            menuPosition="fixed"
            options={conditionOptions}
            defaultValue={defaultValue}
            variant="transparent"
            onChange={(option: ISelectOption) =>
              handleDeviceChange(option, original.deviceId, 'condition')
            }
            isDisabled={
              (loadingDeviceUpdate || original.status === DeviceStatus.SUSPENDED) && !isSuperUser
            }
            containerNoWrap
          />
        );
      }
    },
    {
      Header: intl.formatMessage({ id: 'statusKey' }),
      Cell: ({ row: { original } }: Cell<IDeviceListItem>) => {
        const statusOptions = getDeviceStatusOptions();
        const currentStatus = statusOptions.find((item) => item.value === original.status);
        const statusText = (
          <span>{currentStatus?.label || original.status || TABLE_EMPTY_VALUE}</span>
        );
        if (original.status !== DeviceStatus.ORDERED) return statusText;
        const track = getFulfillmentTrack(original);
        const fulfillmentEvent = getLatestPreShipmentEvent(track);
        if (!track || !fulfillmentEvent) return statusText;
        return (
          <div>
            {statusText}
            <FulfillmentStatusLine
              provider={track.provider}
              event={fulfillmentEvent}
              deviceId={original.deviceId}
            />
          </div>
        );
      }
    },
    {
      Header: intl.formatMessage({ id: 'batteryLevelKey' }),
      align: 'right',
      order: orderBy === 'BATTERY_LEVEL' ? (!desc ? 'asc' : 'desc') : null,
      sort: !isProfile ? () => handleSorting('BATTERY_LEVEL') : undefined,
      Cell: ({ row: { original } }: Cell<IDeviceListItem>) => {
        const batteryLevel = original.batteryLevel ? Number(original.batteryLevel) : undefined;
        // TODO: PHX-907 — use batteryLevelType to decide display format (percentage vs voltage)
        const displayName = original.deviceOrdering?.displayName ?? '';
        const manufacturer = original.deviceOrdering?.manufacturer ?? '';
        let batteryValue = TABLE_EMPTY_VALUE;
        let isLowBattery = false;

        if (batteryLevel) {
          batteryValue = getDeviceStatusValue(batteryLevel, manufacturer);
          isLowBattery = isDeviceBatteryLow(batteryLevel, displayName, manufacturer);
        }

        return batteryValue === TABLE_EMPTY_VALUE ? (
          batteryValue
        ) : (
          <div className="d-flex align-items-center justify-content-end">
            {isLowBattery && (
              <Badge pill bg="warning" className="me-2">
                {intl.formatMessage({ id: 'lowKey' })}
              </Badge>
            )}
            <span>{batteryValue}</span>
          </div>
        );
      }
    },
    {
      Header: intl.formatMessage({ id: 'signalStrengthKey' }),
      order: orderBy === 'SIGNAL_STRENGTH' ? (!desc ? 'asc' : 'desc') : null,
      sort: !isProfile ? () => handleSorting('SIGNAL_STRENGTH') : undefined,
      align: 'right',
      Cell: ({ row: { original } }: Cell<IDeviceListItem>) => {
        return original.signalStrength ? `${original.signalStrength} %` : TABLE_EMPTY_VALUE;
      }
    },
    {
      id: 'actions',
      hide: !showActions,
      width: 60,
      Cell: ({ row }: Cell<IDeviceListItem>) => (
        <div className="d-flex justify-content-end">
          <DropdownBS align="end" className="no-caret">
            <DropdownBS.Toggle variant="link" className="no-caret">
              <ThreeVerticalDotsIcon className="icon" />
            </DropdownBS.Toggle>
            <PortalDropdownMenu>
              {!isProfile && (
                <>
                  {deviceType === DeviceTypes.ASSIGNED ? (
                    <>
                      <DropdownBS.Item
                        onClick={() => setUnassignDevice(row.original)}
                        data-test={`unassignDeviceId-open-modal-${row.original.deviceId}`}
                      >
                        {intl.formatMessage({ id: 'UnassignKey' })}
                      </DropdownBS.Item>
                      <DropdownBS.Item
                        onClick={() => setDeviceToResync(row.original)}
                        disabled={
                          !isDeviceActionable(row.original, 'resync') ||
                          !row.original.imei ||
                          (!!row.original.assignedUid &&
                            row.original.patient?.patientStatus === PatientStatus.ARCHIVED)
                        }
                        data-test={`resync-device-${row.original.deviceId}`}
                      >
                        {intl.formatMessage({ id: 'resyncKey' })}
                      </DropdownBS.Item>
                      {isSuperUser && (
                        <>
                          <DropdownBS.Item
                            onClick={() => setDeviceToReplace(row.original)}
                            disabled={!isDeviceActionable(row.original, 'replace')}
                          >
                            {intl.formatMessage({ id: 'replaceKey' })}
                          </DropdownBS.Item>
                          <DropdownBS.Item
                            onClick={() => registerDevice(row.original)}
                            disabled={
                              !isDeviceActionable(row.original, 'register') || !row.original.imei
                            }
                          >
                            {intl.formatMessage({ id: 'registerKey' })}
                          </DropdownBS.Item>
                          <DropdownBS.Item onClick={() => setDeviceToReplaceImei(row.original)}>
                            {intl.formatMessage({ id: 'replaceImeiKey' })}
                          </DropdownBS.Item>
                          <DropdownBS.Item
                            onClick={() => setDeviceToActivateSim(row.original)}
                            disabled={!isDeviceActionable(row.original, 'activateSim')}
                          >
                            {intl.formatMessage({ id: 'activateSimKey' })}
                          </DropdownBS.Item>
                        </>
                      )}
                    </>
                  ) : deviceType === DeviceTypes.DELETED ? (
                    <DropdownBS.Item
                      onClick={() => setDeviceToRestore(row.original)}
                      data-test={`restore-device-${row.original.deviceId}`}
                    >
                      {intl.formatMessage({ id: 'restoreDeviceKey' })}
                    </DropdownBS.Item>
                  ) : (
                    <>
                      <DropdownBS.Item
                        disabled={
                          row.original.status === DeviceStatus.REPLACED ||
                          row.original.status === DeviceStatus.RETIRED
                        }
                        onClick={() => navigateToDevice(row.original.deviceId)}
                      >
                        {intl.formatMessage({ id: 'assignKey' })}
                      </DropdownBS.Item>
                      <DropdownBS.Item
                        onClick={() => setDeletedDeviceId(row.original.deviceId)}
                        data-test={`remove-${row.original.deviceId}`}
                      >
                        {intl.formatMessage({ id: 'removeKey' })}
                      </DropdownBS.Item>
                    </>
                  )}
                  {isSysop && (
                    <DropdownBS.Item
                      onClick={() => setOrderToCancel(row.original)}
                      disabled={
                        !row.original.purchaseOrder?.purchaseOrderId ||
                        row.original.status !== DeviceStatus.ORDERED
                      }
                      data-test={`cancel-order-${row.original.deviceId}`}
                    >
                      {intl.formatMessage({ id: 'cancelOrderKey' })}
                    </DropdownBS.Item>
                  )}
                </>
              )}
              <DropdownBS.Item {...row.getToggleRowExpandedProps()}>
                {intl.formatMessage({
                  id: row.isExpanded ? 'hideDeviceHistoryKey' : 'showDeviceHistoryKey'
                })}
              </DropdownBS.Item>
              <DropdownBS.Item
                onClick={() => setDeviceForAccessories(row.original)}
                disabled={!row.original.options}
              >
                {intl.formatMessage({
                  id: 'showDeviceAccessoryKey'
                })}
              </DropdownBS.Item>
            </PortalDropdownMenu>
          </DropdownBS>
        </div>
      )
    }
  ];

  useMount(() => {
    searchParams.get('action') === 'add' && setShowAddModal(true);
  });

  const handleLimit = (limit: number) => {
    setLimit(limit);
    setPage(0);
  };

  const getSearchPlaceholder = () => {
    return (
      intl.formatMessage({ id: 'searchByKey' }) +
      ' ' +
      intl.formatMessage({ id: 'patientNameKey' }) +
      ', ' +
      intl.formatMessage({ id: 'imeiKey' }) +
      ', ' +
      intl.formatMessage({ id: 'purchaseOrderKey' }) +
      ', ' +
      intl.formatMessage({ id: 'alternateIdKey' })
    );
  };

  const resetAction = () => {
    setUnassignDevice(null);
    setDeletedDeviceId(null);
    setBulkDeleted([]);
  };

  const handleRestoreSuccess = (deviceId: string) => {
    setDevices(devices.filter((d) => d.deviceId !== deviceId));
    setDeviceToRestore(null);
    toast(
      <ToastNotificationContent
        icon={<CheckIcon className="icon" />}
        text="deviceRestoredSuccessKey"
      />
    );
  };

  const handleCloseBulkModal = (refetch = false) => {
    if (refetch) {
      setRefetch(true);
    }
    setShowBulkOrderModal(false);
  };

  useEffect(() => {
    if (!loading) {
      if (patient?.uid && data?.data) {
        dispatch(updateDevices(data.data));
        setDevices(data.data);
      } else {
        data?.data && setDevices(data.data);
      }
      setTotalDevicesCount(data?.total || null);
    }
  }, [patient?.uid, dispatch, data?.data, loading, data?.total]);

  useEffect(() => {
    if (refetch) {
      resetAction();
      get();
      setRefetch(false);
    }
  }, [refetch, get]);

  useUpdateEffect(() => {
    setPage(0);
    setTotalDevicesCount(null);
  }, [deviceType, selectedGid]);

  const OrdersDropdown = () => {
    return (
      <DropdownButton
        variant="link"
        title={intl.formatMessage({ id: 'ordersKey' })}
        align="end"
        data-test="orders-dropdown"
      >
        <DropdownBS.Item
          onClick={() => setShowExportOrdersModal(true)}
          data-test="download-pending-orders"
        >
          <DownloadIcon className="icon pe-2" />
          <span>{intl.formatMessage({ id: 'downloadPendingOrdersKey' })}</span>
        </DropdownBS.Item>
        <DropdownBS.Item
          onClick={() => setShowShippingConfirmationModal(true)}
          data-test="upload-shipping-confirmation"
        >
          <UploadIcon className="icon pe-2" />
          <span>{intl.formatMessage({ id: 'uploadShippingConfirmationKey' })}</span>
        </DropdownBS.Item>
      </DropdownButton>
    );
  };

  const BulkOrderButton = () => {
    return (
      <DropdownButton
        variant="link"
        title={`+ ${intl.formatMessage({ id: 'orderKey' })}`}
        disabled={bulkOrderDisabled}
        align="end"
        data-test="bulk-order"
      >
        <DropdownBS.Item onClick={() => setShowBulkOrderModal(true)}>
          {intl.formatMessage({ id: 'deviceKey' })}
        </DropdownBS.Item>
        <DropdownBS.Item
          onClick={() => setShowAccessoriesOrderModal(true)}
          disabled={!hasOrderableAccessories}
        >
          {intl.formatMessage({ id: 'accessoriesKey' })}
        </DropdownBS.Item>
      </DropdownButton>
    );
  };

  return (
    <>
      {deviceMgmtDisabledForGroup ? (
        <PermissionDenied sectionName={intl.formatMessage({ id: 'devicesKey' }) + ''} />
      ) : (
        <div>
          {isProfile ? (
            <div className="d-flex align-items-center justify-content-between m-0">
              <Button
                data-test="add-device"
                variant="link m-2 justify-content-end"
                onClick={() => setShowAddModal(true)}
                disabled={patient?.patientStatus === PatientStatus.ARCHIVED}
              >
                + {intl.formatMessage({ id: 'addDeviceKey' })}
              </Button>
            </div>
          ) : isAllGroups ? (
            <Alert variant="primary" className="mt-4">
              {intl.formatMessage({ id: 'groupNotSelectKey' })}
            </Alert>
          ) : (
            <>
              <Nav className="tabs pb-0">
                <Link
                  to={getPath().dashboard.devices(DeviceTypes.ASSIGNED)}
                  onClick={() => setTotalDevicesCount(null)}
                  className={classNames({
                    active: deviceType === DeviceTypes.ASSIGNED
                  })}
                >
                  {`${intl.formatMessage({ id: 'assignedKey' })} ${deviceType === DeviceTypes.ASSIGNED && totalDevicesCount !== null ? `(${totalDevicesCount})` : ''}`}
                </Link>
                <Link
                  to={getPath().dashboard.devices(DeviceTypes.UNASSIGNED)}
                  onClick={() => setTotalDevicesCount(null)}
                  className={classNames({
                    active: deviceType === DeviceTypes.UNASSIGNED
                  })}
                >
                  {`${intl.formatMessage({ id: 'unassignedKey' })} ${deviceType === DeviceTypes.UNASSIGNED && totalDevicesCount !== null ? `(${totalDevicesCount})` : ''}`}
                </Link>
                {isSuperUser && (
                  <Link
                    to={getPath().dashboard.devices(DeviceTypes.DELETED)}
                    onClick={() => setTotalDevicesCount(null)}
                    className={classNames({
                      active: deviceType === DeviceTypes.DELETED
                    })}
                  >
                    {`${intl.formatMessage({ id: 'deletedDevicesKey' })} ${deviceType === DeviceTypes.DELETED && totalDevicesCount !== null ? `(${totalDevicesCount})` : ''}`}
                  </Link>
                )}
              </Nav>
            </>
          )}
          {!isAllGroups && !loading && error && <Error retry={get} />}
          {!isAllGroups && (
            <Container fluid className="bg-white pt-2">
              {/* Hidden while moving out: registering is the destination's action, and a patient
                  migrated onward could otherwise re-register a SIM on its way out. */}
              {canMigratePatients && isProfile && !isMigratingAway && (
                <Row className="mb-2">
                  <Col className="d-flex justify-content-end">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setShowMigrateDevices(true)}
                      data-test="migrate-devices"
                    >
                      {intl.formatMessage({ id: 'migrationMigrateDevicesKey' })}
                    </Button>
                  </Col>
                </Row>
              )}
              {!isProfile && (
                <Row className="mb-2">
                  <Col sm={5}>
                    <DeviceFilters filters={filters} setFilters={setFilters} />
                    {(filters.length || 0) > 0 && (
                      <ActiveDeviceFiltersDisplay
                        activeFilters={filters}
                        clearFilters={clearFilters}
                      />
                    )}
                  </Col>
                  <Col sm={7}>
                    <div className="d-flex justify-content-end">
                      {isSysop && <OrdersDropdown />}
                      {bulkOrderDisabled ? (
                        <OverlayTrigger
                          placement="auto"
                          overlay={
                            <Tooltip id="tooltip-icon-overlay">
                              {intl.formatMessage({
                                id: gid === 'all' ? 'selectAGroupKey' : 'missingPermissionsKey'
                              })}
                            </Tooltip>
                          }
                        >
                          <div>
                            <BulkOrderButton />
                          </div>
                        </OverlayTrigger>
                      ) : (
                        <BulkOrderButton />
                      )}
                    </div>
                  </Col>
                </Row>
              )}
              <Table
                dataTestIdPrefix="devices"
                data={devices}
                columns={columns}
                truncateColumns={[
                  intl.formatMessage({ id: 'groupsKey' }),
                  intl.formatMessage({ id: 'trackingNumberKey' })
                ]}
                loading={loading}
                noDataTxt={
                  isProfile
                    ? `${patient?.lastName}, ${patient?.firstName} ${intl.formatMessage({
                        id: 'currentlyNoDeviceKey'
                      })}`
                    : intl.formatMessage({ id: 'noDeviceDataForCriteria' })
                }
                leftComponent={
                  !isProfile ? (
                    <Row className="w-100">
                      <Col lg={8} className="m-0">
                        <Search
                          placeholder={getSearchPlaceholder()}
                          handleSearch={handleSearch}
                          handleReset={handleSearch}
                          active={!!searchDevices.length}
                          dataTestId="device-search"
                        />
                      </Col>
                    </Row>
                  ) : (
                    <></>
                  )
                }
                setPage={setPage}
                setLimit={(limit) => handleLimit(limit as number)}
                currentPage={page}
                pageCount={data?.totalPages}
                defaultPageSize={limit}
                pagination={!isProfile}
                stickyCols={2}
                stickyColsRight={1}
                onSelectChange={(devices) => dispatch(onChangeSelection({ devices }))}
                autoHeight
                hideMargin={true}
                customExpandComponent={(row: any) => <DeviceHistory device={row.original} />}
              />
            </Container>
          )}
        </div>
      )}
      {unassignDevice && (
        <UnassignDevice
          deviceId={unassignDevice.deviceId}
          isGlucometer={unassignDevice.deviceOrdering?.displayType === 'glucometerTypeKey'}
          deviceName={unassignDevice.deviceOrdering?.displayName}
          imei={unassignDevice.imei}
          patient={unassignDevice.patient}
          onCancel={() => setUnassignDevice(null)}
          onUnassign={() => {
            setDevices(devices.filter((device) => device.deviceId !== unassignDevice.deviceId));
            if (totalDevicesCount !== null) {
              setTotalDevicesCount(totalDevicesCount - 1);
            }
            setUnassignDevice(null);
          }}
        />
      )}
      {showMigrateDevices && patient?.uid && (
        <MigrateDevicesModal
          uid={patient.uid}
          onClose={(migrated) => {
            setShowMigrateDevices(false);
            if (migrated) get();
          }}
        />
      )}
      {(deletedDeviceId || bulkDeleted.length > 0) && (
        <DeleteDevice
          deviceId={deletedDeviceId}
          bulkDeleted={bulkDeleted}
          onClose={() => {
            setDeletedDeviceId(null);
            setBulkDeleted([]);
            setRefetch(true);
          }}
        />
      )}
      {showAddModal && (
        <AddDevice patient={patient} onClose={handleCloseAddModal} onRefetch={setRefetch} />
      )}
      {showBulkOrderModal && <BulkOrderDevices close={handleCloseBulkModal} />}
      {showAccessoriesOrderModal && (
        <BulkOrderAccessories close={() => setShowAccessoriesOrderModal(false)} />
      )}
      {deviceToReplace && (
        <ReplaceDeviceModal
          device={deviceToReplace}
          onClose={() => setDeviceToReplace(null)}
          onSuccess={() => setRefetch(true)}
        />
      )}
      {deviceToResync && (
        <ResyncDeviceModal
          device={deviceToResync}
          onClose={() => setDeviceToResync(null)}
          isSuperUser={isSuperUser}
        />
      )}
      {deviceToReplaceImei && (
        <ReplaceImeiModal
          device={deviceToReplaceImei}
          onClose={() => setDeviceToReplaceImei(null)}
          onSuccess={() => setRefetch(true)}
        />
      )}
      {orderToCancel && (
        <CancelOrderModal
          device={orderToCancel}
          onClose={() => setOrderToCancel(null)}
          onSuccess={() => setRefetch(true)}
        />
      )}
      {deviceToActivateSim && (
        <ActivateSimModal
          device={deviceToActivateSim}
          onClose={() => setDeviceToActivateSim(null)}
        />
      )}
      {showShippingConfirmationModal && (
        <ShippingConfirmationModal onClose={() => setShowShippingConfirmationModal(false)} />
      )}
      {showExportOrdersModal && (
        <ExportPendingOrdersModal onClose={() => setShowExportOrdersModal(false)} />
      )}
      {deviceToRestore && (
        <RestoreDeviceModal
          device={deviceToRestore}
          onClose={() => setDeviceToRestore(null)}
          onSuccess={() => handleRestoreSuccess(deviceToRestore.deviceId)}
        />
      )}
      {deviceForAccessories && (
        <AccessoriesModal
          device={deviceForAccessories}
          patient={patient}
          onClose={() => setDeviceForAccessories(null)}
        />
      )}
    </>
  );
};

export default Devices;
